import { useEffect, useState } from 'react';
import { Modal, Select } from 'antd';
import LocationCascade from '../../../components/LocationCascade';
import PartyLocationDropdown from './PartyLocationDropdown';
import { Field } from './formFields';
import {
  BOUNDARY_AUTHORITIES,
  DEPARTMENTS,
  DESIGNATIONS,
  ROLE_CLASSIFICATIONS,
  toSelectOptions,
  type AssignedRole,
  type RoleScope,
} from './types';

interface Props {
  open: boolean;
  organization?: number;
  roleOptions: { value: number; label: string }[];
  /** Department / Designation options from field_options (fall back to the static lists). */
  departmentOptions: { value: string; label: string }[];
  designationOptions: { value: string; label: string }[];
  /** When set, the modal edits this role; otherwise it adds a new one. */
  initial?: AssignedRole | null;
  onCancel: () => void;
  onSave: (role: AssignedRole) => void;
}

const emptyDraft: AssignedRole = {
  id: '',
  roleId: '',
  roleName: '',
  scopeType: 'functional',
  department: '',
  designation: '',
  boundaryAuthority: 'eci',
  boundaryLevel: '',
  location: '',
  locationLabel: '',
};

/** Role-type dropdown options ("Functional Role" → 'functional', …). */
const ROLE_TYPE_OPTIONS = ROLE_CLASSIFICATIONS.map((r) => ({ value: r.key, label: r.label }));

/** Uppercase field label matching the design (e.g. "SELECT ROLE TYPE *"). */
function ScopeLabel({ children, required }: { children: string; required?: boolean }) {
  return (
    <label className="text-[11px] font-semibold uppercase tracking-[0.4px] text-[#475569]">
      {children}
      {required && <span className="text-[#ef4444]"> *</span>}
    </label>
  );
}

/**
 * "Add Role Assignment" wizard — assign a functional, territorial, or hybrid role scope to the
 * Client Team user (Figma 1427-4455). Territorial / Hybrid roles reveal the Boundary Location
 * Scope box (authority + jurisdiction + electoral boundary level); Functional roles are global.
 */
export default function AssignRoleModal({
  open,
  organization,
  roleOptions,
  departmentOptions,
  designationOptions,
  initial,
  onCancel,
  onSave,
}: Props) {
  const deptOpts = departmentOptions.length ? departmentOptions : toSelectOptions(DEPARTMENTS);
  const desigOpts = designationOptions.length ? designationOptions : toSelectOptions(DESIGNATIONS);
  const [draft, setDraft] = useState<AssignedRole>(emptyDraft);
  const [touched, setTouched] = useState(false);

  // Reset the draft each time the modal opens (fresh add, or the row being edited).
  useEffect(() => {
    if (open) {
      setDraft(initial ?? emptyDraft);
      setTouched(false);
    }
  }, [open, initial]);

  const set = (patch: Partial<AssignedRole>) => setDraft((d) => ({ ...d, ...patch }));
  // Functional → Department + Designation; Territorial → Boundary scope; Hybrid → both.
  const needsDeptDesig = draft.scopeType === 'functional' || draft.scopeType === 'hybrid';
  const needsLocation = draft.scopeType === 'territorial' || draft.scopeType === 'hybrid';
  const isPoAuthority = draft.boundaryAuthority === 'po';
  // The jurisdiction dropdown's label reflects the chosen Boundary Authority (ECI / SEC / PO).
  const authAbbr = ({ eci: 'ECI', sec: 'SEC', po: 'PO' } as Record<string, string>)[
    draft.boundaryAuthority
  ];
  const jurisdictionLabel = isPoAuthority
    ? `Assigned Org Hierarchy (${authAbbr ?? 'PO'})`
    : `Assigned Constituency / Jurisdiction (${authAbbr ?? 'ECI'})`;
  const roleMissing = draft.roleId === '';
  const deptMissing = needsDeptDesig && !draft.department;
  const desigMissing = needsDeptDesig && !draft.designation;
  const locationMissing = needsLocation && !draft.location;
  const canSave = !roleMissing && !deptMissing && !desigMissing && !locationMissing;

  const handleSave = () => {
    if (!canSave) {
      setTouched(true);
      return;
    }
    const roleName = roleOptions.find((o) => o.value === draft.roleId)?.label ?? draft.roleName;
    onSave({
      ...draft,
      roleName,
      // Keep only the fields relevant to the chosen scope.
      department: needsDeptDesig ? draft.department : '',
      designation: needsDeptDesig ? draft.designation : '',
      boundaryLevel: needsLocation ? draft.boundaryLevel : '',
      location: needsLocation ? draft.location : '',
      locationLabel: needsLocation ? draft.locationLabel : '',
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={
        <div className="flex flex-col gap-1">
          <span className="text-lg font-semibold text-ink">Add Role Assignment</span>
          <span className="text-xs font-normal text-[#64748b]">
            Assign functional, territorial, or hybrid role scope to this user.
          </span>
        </div>
      }
      okText="Save"
      onOk={handleSave}
      okButtonProps={{ disabled: !canSave }}
      width={672}
      destroyOnHidden
    >
      <div className="flex flex-col gap-5 py-3">
        {/* Role Type + Role */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <ScopeLabel required>SELECT ROLE TYPE</ScopeLabel>
            <Select
              className="w-full"
              value={draft.scopeType}
              onChange={(v) => set({ scopeType: v as RoleScope })}
              options={ROLE_TYPE_OPTIONS}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <ScopeLabel required>SELECT ROLE</ScopeLabel>
            <Select
              className="w-full"
              placeholder="Select Role"
              showSearch
              optionFilterProp="label"
              status={touched && roleMissing ? 'error' : undefined}
              value={draft.roleId === '' ? undefined : draft.roleId}
              onChange={(v) => set({ roleId: v ?? '' })}
              options={roleOptions}
              allowClear
            />
            {touched && roleMissing && (
              <span className="text-xs font-medium text-[#ba1a1a]">Select a role</span>
            )}
          </div>
        </div>

        {/* Department + Designation — functional / hybrid only */}
        {needsDeptDesig && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              label="Department"
              required
              error={touched && deptMissing ? 'Select a department' : undefined}
            >
              <Select
                className="w-full"
                placeholder="Select Department"
                showSearch
                optionFilterProp="label"
                status={touched && deptMissing ? 'error' : undefined}
                value={draft.department || undefined}
                onChange={(v) => set({ department: v ?? '' })}
                options={deptOpts}
                allowClear
              />
            </Field>
            <Field
              label="Designation"
              required
              error={touched && desigMissing ? 'Select a designation' : undefined}
            >
              <Select
                className="w-full"
                placeholder="Select Designation"
                showSearch
                optionFilterProp="label"
                status={touched && desigMissing ? 'error' : undefined}
                value={draft.designation || undefined}
                onChange={(v) => set({ designation: v ?? '' })}
                options={desigOpts}
                allowClear
              />
            </Field>
          </div>
        )}

        {/* Boundary Location Scope — territorial / hybrid only */}
        {needsLocation && (
          <div className="flex flex-col gap-4 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-4">
            <div className="flex items-center gap-2">
              <span className="size-2 rounded-full bg-brand" />
              <h4 className="text-xs font-bold uppercase tracking-[0.4px] text-[#334155]">
                Boundary Location Scope
              </h4>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-medium leading-4 text-[#475569]">
                Boundary Authority:
              </label>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {BOUNDARY_AUTHORITIES.map((a) => (
                  <label
                    key={a.key}
                    className="flex cursor-pointer items-center gap-2 text-sm text-[#334155]"
                  >
                    <input
                      type="radio"
                      name="modalBoundaryAuthority"
                      checked={draft.boundaryAuthority === a.key}
                      // Switching authority swaps the jurisdiction source, so reset the pick.
                      onChange={() =>
                        set({ boundaryAuthority: a.key, location: '', locationLabel: '' })
                      }
                      className="size-4 accent-brand"
                    />
                    {a.label}
                  </label>
                ))}
              </div>
            </div>

            <Field
              label={jurisdictionLabel}
              required
              error={touched && locationMissing ? 'Select a location' : undefined}
            >
              {isPoAuthority ? (
                // Political Organization → the org's own hierarchy (node tree).
                <PartyLocationDropdown
                  organization={organization}
                  value={draft.location}
                  placeholder="Select Org Hierarchy"
                  invalid={touched && locationMissing}
                  onChange={(key, label) => set({ location: key, locationLabel: label ?? '' })}
                />
              ) : (
                // ECI / SEC → the Election Commission area tree.
                <LocationCascade
                  organization={organization}
                  value={draft.location}
                  source="ec-tree"
                  placeholder="Select Jurisdiction"
                  invalid={touched && locationMissing}
                  initialLabel={draft.locationLabel}
                  onChange={(sel) =>
                    set({
                      location: sel ? String(sel.value) : '',
                      locationLabel: sel?.label ?? '',
                    })
                  }
                />
              )}
            </Field>
          </div>
        )}
      </div>
    </Modal>
  );
}
